import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { EyeIcon, FileTextIcon, InfoIcon, PhoneIcon, PlusIcon, RefreshCcw, SettingsIcon, ShieldIcon, TriangleAlertIcon, UsersIcon, WorkflowIcon } from "lucide-react";
import { createJob, JobCreateRequest, LocationMetadata } from "@service/employer";
import { listCommitmentTypes, listCurrency, listWorkArrangement, payRateFrequencies } from "@constant/app";
import { useCallback, useEffect, useState } from "react";
import { Assessment, FormAssessment, InterviewAssessment } from "@type/assessments";
import { getListAssessment } from "@service/assessment";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Tooltip } from "@component/tooltip";
import { getJobDetail, updateJob } from "@service/job";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import { getCookie, setCookie } from "@util/cookie";
import { Tabs, TabsList, TabsTrigger } from "@component/tabs";
import { useUserProfileContext } from "@context/user_profile";
import { useSubscription } from "@context/subscription_context";
import TextEditor from "@component/rich-editor/v2/text-editor";
import ConfirmSettingsStep from "@container/confirm-setting-step";
import ProgressTrackerJob from "@component/progress-tracker-job";
import BreadcrumbNav from "@component/breadcrumb-job";
import JobRecalculatedDialog from "./dialog/dialog-job-recalculated";
import AccessControlStep from "./job-access-control";
import DialogViewLimit from "./dialog/dialog-view-limit";
import { JobOwnerItem } from "@type/job";
import { Company, SubCompany } from "@type/company";
import { getCompanyInfo } from "@service/user";
import JobPreviewDialog from "./dialog/dialog-job-preview-v2";
import LocationGMap from "@component/location-gmap";
import { GMapPlace } from "@type/google_map";
import { defaultJobAutoRejectionTemplate } from "@constant/email";
import { isValidEmail } from "@util/string";
import { Checkbox } from "@component/checkbox";
import { Switch } from "@component/switch";
import HiringFlowBuilder from "./hiring-flow-builder";
import { defaultHiringFlow, flowsEqual, HiringFlow, HiringFlowStep, isCvDocumentItem, isFlowValid, normalizeFlowForSave, stepGates } from "@type/hiring-flow";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@component/dialog";

interface Props {
    isEditMode?: boolean
}

export default function JobForm(props: Props) {
    const { jobid } = useParams();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const { isJobActionBlocked } = useSubscription();
    // Both create and edit Job are blocked once the workspace is restricted
    // (trial expired + past the 7-day grace window) or the trial CV-screening
    // quota is exhausted — see subscription_context `isJobActionBlocked`.
    const isSubscriptionLimitReached = isJobActionBlocked;
    const [isOpenModalViewLimit, setOpenModalViewLimit] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [useRateRanges, setUseRateRanges] = useState(true);
    const [assessments, setAssessments] = useState<Assessment[]>([]);
    const [isDontShowAgain, setDontShowAgain] = useState<boolean>(getCookie("job_recalculated_dont_show_again"));
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isOpenRecalculatedDialog, setOpenRecalculatedDialog] = useState<boolean>(false);
    const [isLoadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const [selectedAssessment, setSelectedAssessment] = useState<{ type: string; id: string; name: string }>({ type: "", id: "", name: "None" });
    const [selectedInterviewAssessment, setSelectedInterviewAssessment] = useState<{ type: string; id: string; name: string }>({ type: "", id: "", name: "None" });
    const [activeField, setActiveField] = useState<string>("");
    const [jobDetail, setJobDetail] = useState<JobCreateRequest | null>(null);
    const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);
    const [selectedCompanyIndex, setSelectedCompanyIndex] = useState<number>(0);
    const [isShowJobPreview, setShowJobPreview] = useState<boolean>(false);
    const [jobData, setJobData] = useState<JobCreateRequest>({
        job_title: "",
        job_description: "",
        job_type: "Full time",
        department: "",
        requirements: "",
        benefits: "",
        work_arrangement: "Onsite",
        location: "",
        pay_rate_frequency: "Monthly",
        rate_currency: "MYR",
        fixed_rate: 0,
        min_rate: 0,
        max_rate: 0,
        interview_type: "Video",
        is_enabled_wa_call: false,
        referral_bonus: 0,
        referral_currency: "MYR",
        is_public: true,
        is_send_auto_communication: false,
        is_enable_auto_rejection: false,
        is_enable_work_authorization: false,
        work_authorization_metadata: undefined,
        is_enable_application_expiry: true,
        application_expiry_days: 7,
        days_auto_rejection: 0,
        rejection_email_subject: "",
        rejection_email_body: "",
        owner_ids: [],
        is_collaborator: false,
        listing_owners: [],
        job_latitude: 0,
        job_longitude: 0,
    });

    // Customizable Hiring Flow (#3066/#3070) — the wizard's Step 2 state. The
    // flow is only SENT in the job payload when it can't be represented by the
    // legacy fields (assessment_ids / is_enabled_wa_call / eligibility metadata),
    // or when the job is already flow-managed — so uncustomized jobs keep
    // today's exact behavior (BE #3641: omit ⇒ ourteam Default).
    const [hiringFlow, setHiringFlow] = useState<HiringFlow>(defaultHiringFlow());
    const [initialHiringFlow, setInitialHiringFlow] = useState<HiringFlow>(defaultHiringFlow());
    // #3202 (BE ourteam-backend#3913): per-job "new hiring flow" flag. Create mode
    // defaults ON (the BE's new-job default); edit mode is overwritten from the job's
    // persisted value in the read below — existing jobs are OFF and the FE never
    // forces them on. Only meaningful when the company rollout flag is on.
    const [newHiringFlowEnabled, setNewHiringFlowEnabled] = useState<boolean>(true);
    // #3147: submit-time warning dialog when a hiring-flow job leaves the CV
    // uncollected (no CV-at-apply + no Document Collection CV step). Same UX in
    // create and edit; the user must confirm ("Save anyway") to proceed.
    const [cvDocWarnOpen, setCvDocWarnOpen] = useState<boolean>(false);
    const [jobHasStoredFlow, setJobHasStoredFlow] = useState<boolean>(false);

    const loadAssessment = () => {
        getListAssessment().then((resp) => {
            if (resp.status === 200) {
                setAssessments(resp.data.data);
            }
        })
    }

    /**
     * True when the flow is exactly what the legacy job fields already encode
     * (one AI interview → optional form → optional eligibility, no gates, all
     * auto-advance, web channel, default entry). Such flows are NOT sent as
     * `hiring_flow` — the legacy fields carry them, exactly as today.
     */
    const isLegacyRepresentable = (flow: HiringFlow): boolean => {
        if (flow.channel !== "web" || flow.entry.auto_start || !flow.entry.cv_required_at_apply) return false;
        // Chat-native (#3090) has no legacy field to carry it — any flow using
        // it must be sent as hiring_flow, not collapsed onto the legacy fields.
        if (flow.chat_native || flow.steps.some((s) => s.type === "ai_interview" && s.mode === "chat")) return false;
        const types = flow.steps.map((s) => s.type);
        const allowedOrders = [
            ["ai_interview"],
            ["ai_interview", "form"],
            ["ai_interview", "eligibility_check"],
            ["ai_interview", "form", "eligibility_check"],
        ];
        if (!allowedOrders.some((order) => JSON.stringify(order) === JSON.stringify(types))) return false;
        return flow.steps.every((s) => stepGates(s).length === 0 && (s.advance || "auto") === "auto");
    };

    /** Rebuild the flow a legacy (non-flow-managed) job encodes today. */
    const synthesizeLegacyFlow = (resp: JobCreateRequest, version: number): HiringFlow => {
        const steps: HiringFlowStep[] = [];
        const interview = (resp.assessment_ids || []).find((a) => a.type === "Interview");
        const form = (resp.assessment_ids || []).find((a) => a.type === "Form");
        steps.push({
            id: "s1",
            type: "ai_interview",
            advance: "auto",
            assessment_id: interview?.id || undefined,
            ...(resp.is_enabled_wa_call ? { mode: "wa_call", fallback: "web" } : {}),
        });
        if (form) {
            steps.push({ id: "s2", type: "form", advance: "auto", assessment_id: form.id });
        }
        const collects = resp.work_authorization_metadata || {};
        if (resp.is_enable_work_authorization || Object.values(collects).some(Boolean)) {
            steps.push({ id: "s3", type: "eligibility_check", advance: "auto" });
        }
        return {
            version,
            template: "ourteam_default",
            channel: "web",
            entry: { auto_start: false, cv_required_at_apply: true },
            steps,
        };
    };

    /**
     * Single entry point for builder edits: stamps template provenance and keeps
     * the legacy fields (selected assessments, wa-call toggle) in sync so the
     * existing payload/preview/recalc-dialog logic keeps working unchanged.
     */
    const applyFlowChange = (flow: HiringFlow) => {
        const next: HiringFlow = {
            ...flow,
            template: isLegacyRepresentable(flow)
                ? "ourteam_default"
                : (flow.template === "ourteam_default" ? "custom" : flow.template),
        };
        setHiringFlow(next);

        // Legacy sync — first AI-interview step drives the job's Interview
        // assessment; first form step drives the Form assessment.
        const interviewStep = next.steps.find((s) => s.type === "ai_interview");
        const interviewId = interviewStep?.assessment_id || "";
        if (interviewId !== selectedInterviewAssessment.id) {
            if (interviewId) {
                const found = assessments.find((a) => a.id === interviewId && a.type === "Interview") as InterviewAssessment | undefined;
                setSelectedInterviewAssessment({ type: "Interview", id: interviewId, name: found?.content.interviewName || "" });
            } else {
                setSelectedInterviewAssessment({ type: "", id: "", name: "None" });
            }
            onHandleBlurAsessment("Interview", interviewId || "None");
        }
        const formStep = next.steps.find((s) => s.type === "form");
        const formId = formStep?.assessment_id || "";
        if (formId !== selectedAssessment.id) {
            if (formId) {
                const found = assessments.find((a) => a.id === formId && a.type === "Form") as FormAssessment | undefined;
                setSelectedAssessment({ type: "Form", id: formId, name: found?.content.title || "" });
            } else {
                setSelectedAssessment({ type: "", id: "", name: "None" });
            }
            onHandleBlurAsessment("Form", formId || "None");
        }
        const hasWaCall = next.steps.some((s) => s.type === "ai_interview" && s.mode === "wa_call");
        if (!!jobData.is_enabled_wa_call !== hasWaCall) {
            updateFormData("is_enabled_wa_call", hasWaCall);
        }
    };

    useEffect(() => {
        if (jobid) {
            setLoading(true);
            getJobDetail(jobid).then((resp) => {
                if (resp) {
                    const processedWorkAuthMetadata = resp.is_enable_work_authorization && !resp.work_authorization_metadata ? {
                        collect_employment_status: true,
                        collect_expected_salary: true,
                        collect_start_availability: true,
                        collect_current_salary: true,
                        collect_relocation_willingness: true,
                        collect_weekend_shift_willingness: true,
                    } : resp.work_authorization_metadata;

                    setJobDetail({
                        ...resp as JobCreateRequest,
                        work_authorization_metadata: processedWorkAuthMetadata,
                    });

                    setJobData({
                        ...resp as JobCreateRequest,
                        rejection_email_subject: defaultJobAutoRejectionTemplate().subject,
                        rejection_email_body: defaultJobAutoRejectionTemplate().content,
                        is_enable_work_authorization: resp.is_enable_work_authorization,
                        work_authorization_metadata: processedWorkAuthMetadata,
                    });

                    if (resp.min_rate && resp.max_rate) {
                        setUseRateRanges(true);
                    }

                    for (const assessment of resp.assessment_ids || []) {
                        if (assessment.type === "Form") {
                            setSelectedAssessment({ type: assessment.type, id: assessment.id, name: assessment.name });
                        } else if (assessment.type === "Interview") {
                            setSelectedInterviewAssessment({ type: assessment.type, id: assessment.id, name: assessment.name });
                        }
                    }

                    // Hiring Flow prefill (#3070): use the stored flow when the
                    // job is flow-managed; otherwise rebuild the flow the legacy
                    // fields encode so the builder shows the real journey.
                    // The BE resolves `hiring_flow` to the ourteam Default even for
                    // uncustomized jobs, so "flow-managed" means a stored flow whose
                    // template isn't the untouched default. For legacy jobs prefer the
                    // synthesized flow — it includes the form/eligibility steps the
                    // job actually runs today.
                    const detail = resp as JobCreateRequest;
                    const hasStoredFlow = !!detail.hiring_flow
                        && (detail.hiring_flow.steps || []).length > 0
                        && detail.hiring_flow.template !== "ourteam_default";
                    const loadedFlow = hasStoredFlow
                        ? detail.hiring_flow as HiringFlow
                        : synthesizeLegacyFlow(detail, detail.flow_version || 1);
                    setJobHasStoredFlow(hasStoredFlow);
                    setHiringFlow(loadedFlow);
                    setInitialHiringFlow(loadedFlow);
                    // #3202: read the per-job flag as persisted — existing jobs are OFF
                    // (absent ⇒ false); never force ON on edit.
                    setNewHiringFlowEnabled(!!detail.new_hiring_flow_enabled);
                }
            }).catch((error) => {
                catchErrorLog(`Failed to fetch job details: ${error}`)
            }).finally(() => {
                setLoading(false);
            })
        }
    }, [jobid])

    useEffect(() => {
        if (jobData && companyInfo) {
            const companyIndex = (companyInfo.company_metadata?.companies || []).findIndex((company: SubCompany) => company.name === jobData.company_name);
            setSelectedCompanyIndex(companyIndex !== -1 ? companyIndex : 0);
        }
    }, [jobData, companyInfo])

    useEffect(() => {
        loadAssessment()
    }, [])

    useEffect(() => {
        if (!isLoadingUserProfile && userInfo) {
            getCompanyInfo(userInfo.company_id).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);
                    if ((resp.data.data.company_metadata?.companies || []).length > 0) {
                        setJobData((prev => {
                            return {
                                ...prev,
                                company_name: (resp.data.data.company_metadata?.companies || []).find((company: SubCompany) => company.is_main)?.name || "",
                            }
                        }));
                    }
                }
            })
        }
    }, [isLoadingUserProfile, userInfo])

    const updateFormData = (key: keyof JobCreateRequest, value: string | number | undefined | boolean | JobOwnerItem[] | LocationMetadata) => {
        setJobData((prev) => ({ ...prev, [key]: value }));
    };

    const updateWorkAuthorizationMetadata = (key: keyof NonNullable<JobCreateRequest["work_authorization_metadata"]>, value: boolean) => {
        setJobData((prev) => ({
            ...prev,
            work_authorization_metadata: {
                ...prev.work_authorization_metadata,
                [key]: value,
            },
        }));
    };

    const onHandleBlur = useCallback((key: keyof JobCreateRequest, value: string | number | undefined | boolean | JobOwnerItem[]) => {
        if (isDontShowAgain) return;

        if (props.isEditMode && jobDetail) {
            if (jobDetail[key] !== value) {
                setActiveField(key);
                setOpenRecalculatedDialog(true);
            }
        }
    }, [props.isEditMode, jobData, isDontShowAgain, jobDetail]);

    const onHandleBlurAsessment = useCallback((typeAssessment: string, value: string) => {
        if (isDontShowAgain) return;

        if (props.isEditMode && jobDetail) {
            if (typeAssessment === "Form" && jobDetail.assessment_ids?.findIndex(item => item.type === "Form" && item.id === value) === -1) {
                setActiveField("Form");
                setOpenRecalculatedDialog(true);
            } else if (typeAssessment === "Interview" && jobDetail.assessment_ids?.findIndex(item => item.type === "Interview" && item.id === value) === -1) {
                setActiveField("Interview");
                setOpenRecalculatedDialog(true);
            }
        }
    }, [props.isEditMode, jobDetail, isDontShowAgain]);

    const onCancelEdit = () => {
        if (activeField === "Form" || activeField === "Interview") {
            setSelectedAssessment(jobDetail?.assessment_ids?.find(item => item.type === "Form") ? { type: "Form", id: jobDetail?.assessment_ids?.find(item => item.type === "Form")?.id || "", name: jobDetail?.assessment_ids?.find(item => item.type === "Form")?.name || "" } : { type: "", id: "", name: "None" });
            setSelectedInterviewAssessment(jobDetail?.assessment_ids?.find(item => item.type === "Interview") ? { type: "Interview", id: jobDetail?.assessment_ids?.find(item => item.type === "Interview")?.id || "", name: jobDetail?.assessment_ids?.find(item => item.type === "Interview")?.name || "" } : { type: "", id: "", name: "None" });
            // Keep the builder in sync with the reverted assessment selection.
            setHiringFlow(initialHiringFlow);
        }

        setJobData((prev) => ({ ...prev, [activeField]: jobDetail ? jobDetail[activeField as keyof JobCreateRequest] : "" }));
        setActiveField("");
        setOpenRecalculatedDialog(false);
    }

    const onConfirmEdit = useCallback(() => {
        setActiveField("");
        setOpenRecalculatedDialog(false);

        if (isDontShowAgain) {
            setCookie("job_recalculated_dont_show_again", "true", 14);
        }
    }, [isDontShowAgain])

    const handleSubmit = (force = false) => {
        // BE #3976 (#3236): a WhatsApp-first flow may screen candidates fully
        // in-chat — no AI Interview step, hence no interview assessment to
        // require. Every other case keeps the guard.
        const waFirstNoInterview =
            hiringFlowEnabled
            && newHiringFlowEnabled
            && hiringFlow.channel === "whatsapp_first"
            // With company WhatsApp OFF the BE coerces the flow to web and
            // re-applies the AI-interview requirement (#3257 BE update).
            && companyWhatsappEnabled
            && !hiringFlow.steps.some((s) => s.type === "ai_interview");
        if (!selectedInterviewAssessment.id && !waFirstNoInterview) {
            snackbar.showError("Please select an AI interview before submitting.");
            return;
        }

        // #3147: warn once (must confirm) before saving a hiring-flow job that
        // leaves the CV uncollected. Applies to create AND edit.
        if (missingCvDocStep && !force) {
            setCvDocWarnOpen(true);
            return;
        }

        setLoadingSubmit(true);

        const assessmentIds = [];
        if (selectedAssessment && selectedAssessment.id) {
            assessmentIds.push({
                type: selectedAssessment.type,
                id: selectedAssessment.id,
                name: selectedAssessment.name
            });
        }

        if (selectedInterviewAssessment && selectedInterviewAssessment.id) {
            assessmentIds.push({
                type: selectedInterviewAssessment.type,
                id: selectedInterviewAssessment.id,
                name: selectedInterviewAssessment.name
            });
        }

        // Send `hiring_flow` only when the company flag is ON (#3084 — a flag-OFF
        // company's payload must NEVER contain hiring_flow; the BE 403s it) AND
        // the job is (or becomes) flow-managed — legacy-representable flows stay
        // on the legacy fields so uncustomized jobs behave exactly as today
        // (BE #3641: omit ⇒ ourteam Default).
        // #3202: the per-job toggle OFF keeps the job on the legacy flow (config
        // gating), so never send `hiring_flow` in that case.
        const shouldSendFlow = hiringFlowEnabled && newHiringFlowEnabled && (jobHasStoredFlow || !isLegacyRepresentable(hiringFlow));

        if (props.isEditMode) {
            if (!jobid) return;

            if (!isLoadingUserProfile && userInfo?.level === "free_trial") {
                // Show dialog informing user about profile limit
                setOpenModalViewLimit(true);
                return;
            }
            setOpenRecalculatedDialog(false);

            const updatePayload: Partial<JobCreateRequest> = {
                ...jobData,
                hiring_flow: shouldSendFlow ? normalizeFlowForSave(hiringFlow) : undefined,
                // #3202: round-trip the per-job flag only where the company rollout is
                // on (a flag-OFF company must never carry it); send what HR set, no default.
                new_hiring_flow_enabled: hiringFlowEnabled ? newHiringFlowEnabled : undefined,
                assessment_ids: assessmentIds,
                is_public: jobData.is_public,
                listing_owners: (jobData.listing_owners || []).filter((owner) => owner.status === "invited") || [],
                days_auto_rejection: jobData.is_enable_auto_rejection ? jobData.days_auto_rejection : undefined,
                application_expiry_days: jobData.is_enable_application_expiry ? jobData.application_expiry_days : undefined,
                outgoing_email: jobData.outgoing_email,
            };

            // Only include rejection_email_body if auto rejection is enabled
            if (!jobData.is_enable_auto_rejection) {
                delete updatePayload.rejection_email_body;
                delete updatePayload.rejection_email_subject;
            }

            // Only include outgoing_email if changed from original
            if (jobDetail && JSON.stringify(jobData.outgoing_email) === JSON.stringify(jobDetail.outgoing_email)) {
                delete updatePayload.outgoing_email;
            }

            updateJob(jobid || "", updatePayload as unknown as JobCreateRequest).then((resp) => {
                if (resp) {
                    snackbar.showSuccess("Job updated successfully!");
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000)
                } else {
                    snackbar.showError("Failed to update job. Please try again.");
                }
            }).catch((error) => {
                // updateJob rethrows the axios error, so the BE's reason is
                // reachable here — show it instead of a generic retry prompt
                // that hides why the save was rejected.
                const beMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
                snackbar.showError(beMessage || "Failed to update job. Please try again.");
                catchErrorLog(`Failed to updated job: ${error}`);
            }).finally(() => {
                setLoadingSubmit(false);
            })
        } else {
            if (!isLoadingUserProfile && userInfo?.level === "free_trial") {
                // Show dialog informing user about profile limit
                setOpenModalViewLimit(true);
                return;
            }

            createJob({
                ...jobData,
                hiring_flow: shouldSendFlow ? normalizeFlowForSave(hiringFlow) : undefined,
                // #3202: new jobs default ON (toggle above); only send when the company
                // rollout is on. HR's setting round-trips; the FE injects no default.
                new_hiring_flow_enabled: hiringFlowEnabled ? newHiringFlowEnabled : undefined,
                assessment_ids: assessmentIds,
                is_public: jobData.is_public,
                days_auto_rejection: jobData.is_enable_auto_rejection ? jobData.days_auto_rejection : undefined,
                application_expiry_days: jobData.is_enable_application_expiry ? jobData.application_expiry_days : undefined,
                outgoing_email: jobData.outgoing_email || {
                    sender_name: companyInfo?.company_metadata?.outgoing_email?.is_job_specific_outgoing_email ? jobData.company_name : companyInfo?.company_metadata?.outgoing_email?.sender_name || jobData.company_name,
                    default_reply_to: companyInfo?.company_metadata?.outgoing_email?.is_job_specific_outgoing_email ? userInfo?.email : companyInfo?.company_metadata?.outgoing_email?.default_reply_to || userInfo?.email,
                },
            }).then((resp) => {
                if (resp.success) {
                    snackbar.showSuccess("Job created successfully!");
                    setTimeout(() => {
                        navigate("/jobs");
                    }, 1000)
                    return;
                }
                // THIS is the API-error path, not the .catch below: createJob
                // handles its own errors and RESOLVES with { success: false,
                // message } (services/employer/index.ts), so a 4xx/5xx never
                // rejects. Without this branch a failed create showed nothing
                // at all — the spinner stopped and the form just sat there.
                // `resp.message` is the BE's own message, already extracted by
                // the service (falling back to "Request timed out" past the 2
                // minute limit); surface it verbatim rather than replacing the
                // reason with a generic retry prompt.
                snackbar.showError(resp.message || "Failed to create job. Please try again.");
                catchErrorLog(`Failed to create job (status ${resp.status}): ${resp.message}`);
            }).catch((error) => {
                // Unexpected throw only (a bug in the handler above) — the
                // service does not reject on API failures.
                snackbar.showError("Failed to create job. Please try again.");
                catchErrorLog(`Failed to create job: ${error}`);
            }).finally(() => {
                setLoadingSubmit(false);
            })
        }
    };

    const getBreadcrumbItems = () => {
        const baseItems = [{ label: "Jobs", href: "/jobs" }];
        if (props.isEditMode) {
            return [
                ...baseItems,
                { label: jobData.job_title },
                { label: "Edit" },
            ];
        }
        return [
            ...baseItems,
            { label: "Create Job", href: "/jobs/create" },
        ];
    };

    const handleNext = () => {
        scrollTo(0, 0);
        if (currentStep < 6) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        scrollTo(0, 0);
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Per-company rollout flag (#3084, BE #3659): edit mode reads it from the
    // job detail (company_hiring_flow_enabled), create mode from the company
    // (hiring_flow_enabled). Absent on an old BE ⇒ OFF — every flow surface
    // hides and the wizard is pixel-identical to the pre-epic form.
    const hiringFlowEnabled = !!(jobDetail?.company_hiring_flow_enabled ?? companyInfo?.hiring_flow_enabled);
    // Company WhatsApp flag (#3257): with it OFF the BE coerces whatsapp_first to
    // web — custom_message and the WA-first no-interview allowance need it ON.
    const companyWhatsappEnabled = !!(jobDetail?.company_whatsapp_enabled ?? companyInfo?.whatsapp_enabled);

    // Flow check gates Step 2's Next (and Save) — mirrors the BE save-time
    // validation so an invalid flow never reaches the request (#3070).
    // Flag OFF — or the per-job toggle OFF (#3202) — ⇒ the builder isn't rendered
    // (legacy screening flow shown), so the check never blocks.
    const flowValid = !hiringFlowEnabled || !newHiringFlowEnabled || isFlowValid(hiringFlow, { waCallAvailable: !!userInfo?.maya_enabled, companyWhatsappEnabled });
    // #3147: the CV is left uncollected when hiring flow is on, CV isn't required
    // at apply, and no Document Collection step has a CV/resume item. Not a hard
    // block — a confirm dialog at submit (create + edit).
    const missingCvDocStep =
        hiringFlowEnabled &&
        !hiringFlow.entry?.cv_required_at_apply &&
        !hiringFlow.steps.some(
            (s) => s.type === "documents" && (s.items || []).some(isCvDocumentItem),
        );

    const isDisabledNext = jobData.job_title.trim() === "" || jobData.job_description.trim() === "" || jobData.location === "" || jobData.department === "" || jobData.company_name === "" || jobData.job_type === "" || jobData.pay_rate_frequency === "" || (jobData.is_enable_auto_rejection && (jobData.rejection_email_subject === "" || jobData.rejection_email_body === "")) || (currentStep === 2 && !flowValid);

    const tabs = [
        { id: "details", title: "Details", step: 1, icon: FileTextIcon, description: "Basic job information", },
        // Flag OFF (#3084) — or the per-job "new hiring flow" toggle OFF (#3202) —
        // Step 2 stays the pre-epic flow, now labelled "Screening Flow". "Hiring
        // Flow" is the epic's own name and is not part of the assessment ->
        // screening rename.
        hiringFlowEnabled && newHiringFlowEnabled
            ? { id: "application", title: "Hiring Flow", step: 2, icon: WorkflowIcon, description: "Design how candidates move through your hiring process", }
            : { id: "application", title: "Screening Flow", step: 2, icon: UsersIcon, description: "Screening process", },
        { id: "access", title: "Access Control", step: 3, icon: ShieldIcon, description: "Manage collaborators for your job listing", },
        { id: "settings", title: "Settings", step: 4, icon: SettingsIcon, description: "Finalize listing settings", },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">Loading job data...</p>
            </div>
        )
    }

    const hasChangeEditButton = (jobData.job_title !== jobDetail?.job_title) || (jobData.job_description !== jobDetail?.job_description) || (jobData.location !== jobDetail?.location) || (jobData.department !== jobDetail?.department) || (jobData.job_type !== jobDetail?.job_type) || (jobData.pay_rate_frequency !== jobDetail?.pay_rate_frequency) || (jobData.rate_currency !== jobDetail?.rate_currency) || (jobData.fixed_rate !== jobDetail?.fixed_rate) || (jobData.min_rate !== jobDetail?.min_rate) || (jobData.max_rate !== jobDetail?.max_rate) || (jobData.referral_bonus !== jobDetail?.referral_bonus) || (jobData.referral_currency !== jobDetail?.referral_currency) || (selectedAssessment.id !== (jobDetail?.assessment_ids?.find(item => item.type === "Form") ? jobDetail?.assessment_ids?.find(item => item.type === "Form")?.id : "")) || (selectedInterviewAssessment.id !== (jobDetail?.assessment_ids?.find(item => item.type === "Interview") ? jobDetail?.assessment_ids?.find(item => item.type === "Interview")?.id : "")) || (jobData.rejection_email_subject !== jobDetail?.rejection_email_subject) || (jobData.rejection_email_body !== jobDetail?.rejection_email_body) || (jobData.is_enable_auto_rejection !== jobDetail?.is_enable_auto_rejection) || (jobData.days_auto_rejection !== jobDetail?.days_auto_rejection) || (jobData.is_send_auto_communication !== jobDetail?.is_send_auto_communication) || (jobData.is_public !== jobDetail?.is_public) || (JSON.stringify(jobData.listing_owners) !== JSON.stringify(jobDetail?.listing_owners)) || jobData.job_status !== jobDetail?.job_status || jobData.work_arrangement !== jobDetail?.work_arrangement || jobData.is_enable_work_authorization !== jobDetail?.is_enable_work_authorization || jobData.company_name !== jobDetail?.company_name || (jobData.is_enable_auto_rejection !== jobDetail?.is_enable_auto_rejection && jobData.rejection_email_subject !== jobDetail.rejection_email_subject && jobData.rejection_email_body !== jobDetail?.rejection_email_body) || (jobData.is_enable_application_expiry !== jobDetail?.is_enable_application_expiry) || (jobData.application_expiry_days !== jobDetail?.application_expiry_days) || (jobData.outgoing_email?.sender_name !== jobDetail?.outgoing_email?.sender_name) || (jobData.outgoing_email?.default_reply_to !== jobDetail?.outgoing_email?.default_reply_to) || (JSON.stringify(jobData.work_authorization_metadata) !== JSON.stringify(jobDetail?.work_authorization_metadata)) || !flowsEqual(hiringFlow, initialHiringFlow);

    return (
        <>
            {/* Breadcrumb in main content area */}
            <div className="mb-6 flex flex-row gap-2 justify-between items-center">
                <BreadcrumbNav items={getBreadcrumbItems()} />
                <div className="flex items-center gap-3">
                    {/* #3202: per-job "new hiring flow" toggle, left of Preview. Only when
                        the company rollout is on. OFF ⇒ Step 2 shows the legacy Screening
                        Flow and no hiring_flow is sent. */}
                    {hiringFlowEnabled && (
                        <Tooltip
                            side="left"
                            content={<p>Run this job on the customizable hiring flow. Turn it off to keep it on the current (legacy) screening flow.</p>}
                        >
                            <div className="flex items-center gap-2 rounded-md border border-gray-300 px-3 h-9">
                                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">New hiring flow</span>
                                <Switch
                                    checked={newHiringFlowEnabled}
                                    onCheckedChange={setNewHiringFlowEnabled}
                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                />
                            </div>
                        </Tooltip>
                    )}
                    <Tooltip
                        side="left"
                        content={<p>Preview how your job will appear to candidates.</p>}
                    >
                        <Button
                            variant="outline"
                            onClick={() => setShowJobPreview(true)}
                            className="border-gray-300 hover:bg-gray-50"
                        >
                            <EyeIcon className="h-4 w-4" />
                            Preview Job Flows
                        </Button>
                    </Tooltip>
                    {props.isEditMode && (
                        <Button
                            onClick={() => handleSubmit()}
                            disabled={isLoadingSubmit || !hasChangeEditButton || jobData.is_collaborator || isDisabledNext || !flowValid || isSubscriptionLimitReached}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {isLoadingSubmit ? "Please wait..." : "Save Changes"}
                        </Button>
                    )}
                </div>
            </div>

            {props.isEditMode && (
                <div className="mb-6">
                    <Tabs
                        value={String(currentStep)}
                        onValueChange={(value: string) => setCurrentStep(Number(value))}
                        className="w-full sm:w-auto"
                    >
                        <TabsList className="flex w-auto mb-8 bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
                            {tabs.map((tab, key) => {
                                const IconComponent = tab.icon;
                                if (tab.id === "access" && userInfo?.permission === "member") return null;
                                return (
                                    <TabsTrigger
                                        key={key}
                                        value={String(tab.step)}
                                        className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                                        style={{
                                            paddingTop: "8px",
                                            paddingRight: "24px",
                                            paddingBottom: "12px",
                                            paddingLeft: "24px",
                                        }}
                                    >
                                        <IconComponent
                                            className={cn(
                                                "w-4 h-4 transition-colors",
                                                (key + 1 === currentStep) ? "text-blue-600" : "text-gray-500"
                                            )}
                                        />
                                        {tab.title}
                                    </TabsTrigger>
                                )
                            })}
                        </TabsList>
                    </Tabs>
                </div>
            )}

            {currentStep === 1 ? (
                <div className="space-y-6 pb-20 min-h-screen">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Job Details
                        </h2>
                        <p className="text-gray-600">Describe your new opportunity.</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
                            <Input
                                id="title"
                                placeholder="ex: Mathematics Intelligence Engineer, Legal Intelligence Analyst"
                                value={jobData.job_title}
                                onChange={(e) => updateFormData("job_title", e.target.value)}
                                onBlur={(e) => onHandleBlur("job_title", e.target.value)}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="WorkArrangement">Work Arrangement <span className="text-red-500">*</span></Label>
                            <Select
                                value={jobData.work_arrangement || ""}
                                onValueChange={(value: string) => {
                                    updateFormData("work_arrangement", value);
                                    onHandleBlur("work_arrangement", value);
                                }}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            >
                                <SelectTrigger className="w-full border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:bg-gray-800">
                                    <SelectValue placeholder="Select job commitment type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {listWorkArrangement.map((item) => (
                                        <SelectItem key={item} value={item}>{item}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="location">Location <span className="text-red-500">*</span></Label>
                            <LocationGMap
                                defaultValue={jobData.location}
                                onChange={(value: string, attribute?: GMapPlace) => {
                                    console.log("Selected location:", value, attribute);
                                    updateFormData("location", value);
                                    updateFormData("location_metadata", {
                                        formattedAddress: attribute ? attribute.formattedAddress : "",
                                        googleMapsUri: attribute ? attribute.googleMapsUri : "",
                                        country: attribute ? attribute.country : "",
                                    });
                                    updateFormData("job_latitude", attribute ? attribute.location.latitude : 0);
                                    updateFormData("job_longitude", attribute ? attribute.location.longitude : 0);
                                }}
                                onBlur={(value: string) => onHandleBlur("location", value)}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            />
                        </div>
                    </div>
                    {companyInfo && !companyInfo.is_byoc ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <Label htmlFor="department">Department <span className="text-red-500">*</span></Label>
                                <Input
                                    id="department"
                                    placeholder="ex: Engineering, Marketing, Sales"
                                    value={jobData.department}
                                    onChange={(e) => updateFormData("department", e.target.value)}
                                    onBlur={(e) => onHandleBlur("department", e.target.value)}
                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor="company" className="text-sm font-medium">
                                            Company <span className="text-red-500">*</span>
                                        </Label>
                                        <Tooltip side="right" content={(
                                            <p>
                                                Choose which company this job belongs to. You can
                                                manage your companies in your Company Settings.
                                            </p>
                                        )}>
                                            <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
                                        </Tooltip>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Link target="_blank" to="/setting?tab=company-information">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-auto px-2 py-1 text-blue-600 hover:bg-transparent hover:text-blue-600 hover:underline flex items-center gap-1.5"
                                            >
                                                <SettingsIcon className="h-4 w-4 text-blue-600" />

                                                <span className="text-sm">Manage Companies</span>
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                                <Select
                                    value={jobData.company_name}
                                    onValueChange={(value) => {
                                        updateFormData("company_name", value);
                                        // Reset department when company changes
                                        if (jobData.department) {
                                            updateFormData("department", "");
                                        }
                                        const selectedIndex = companyInfo?.company_metadata?.companies.findIndex((company) => company.name.toLowerCase() === value.toLowerCase()) || 0;
                                        setSelectedCompanyIndex(selectedIndex);
                                    }}
                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                >
                                    <SelectTrigger className="h-10">
                                        <SelectValue placeholder="Select a company" />
                                    </SelectTrigger>
                                    {companyInfo && (
                                        <SelectContent>
                                            {(companyInfo.company_metadata?.companies || []).length > 0 ? (
                                                <>
                                                    {companyInfo.company_metadata?.companies.map((company, index) => (
                                                        <>
                                                            {
                                                                company.is_main ? (
                                                                    <SelectItem value={company.name} key={index}>
                                                                        <div className="flex items-center gap-2">
                                                                            <span>{company.name}</span>
                                                                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-2xl text-xs font-medium">
                                                                                Main
                                                                            </span>
                                                                        </div>
                                                                    </SelectItem>
                                                                ) : (
                                                                    <SelectItem value={company.name} key={index}>{company.name}</SelectItem>
                                                                )
                                                            }
                                                        </>
                                                    ))}
                                                </>
                                            ) : (
                                                <SelectItem value={companyInfo?.company_name || ""}>
                                                    <div className="flex items-center gap-2">
                                                        <span>{companyInfo?.company_name || ""}</span>
                                                        <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-2xl text-xs font-medium">
                                                            Main
                                                        </span>
                                                    </div>
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    )}
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Label
                                            htmlFor="department"
                                            className={`text-sm font-medium ${!jobData.company_name ? "text-muted-foreground" : ""}`}
                                        >
                                            Department <span className="text-red-500">*</span>
                                        </Label>
                                        <Tooltip side="right" content={(
                                            <p>
                                                Select the department for this job. You can manage
                                                your departments in your Company Settings.
                                            </p>
                                        )}>
                                            <InfoIcon className="h-4 w-4 text-blue-500 cursor-help" />
                                        </Tooltip>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Link target="_blank" to="/setting?tab=company-information">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-auto px-2 py-1 text-blue-600 hover:bg-transparent hover:text-blue-600 hover:underline flex items-center gap-1.5"
                                            >
                                                <SettingsIcon className="h-4 w-4 text-blue-600" />

                                                <span className="text-sm">Manage Departments</span>
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                                <Select
                                    value={jobData.department}
                                    onValueChange={(value) => updateFormData("department", value)}
                                    disabled={!jobData.company_name || jobData.is_collaborator || isSubscriptionLimitReached}
                                >
                                    <SelectTrigger
                                        className={`h-10 ${!jobData.company_name ? "opacity-50 cursor-not-allowed" : ""}`}
                                    >
                                        <SelectValue
                                            placeholder={
                                                jobData.company_name
                                                    ? "Select a department"
                                                    : "Select a company first"
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {companyInfo && companyInfo.company_metadata?.companies && (companyInfo.company_metadata?.companies[selectedCompanyIndex]?.departments || []).length > 0 ? (
                                            <>
                                                {(companyInfo.company_metadata?.companies[selectedCompanyIndex]?.departments || []).map((department, index) => (
                                                    <SelectItem value={department} key={index}>{department}</SelectItem>
                                                ))}
                                            </>
                                        ) : (
                                            <SelectItem value="General">General</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="description">Job Description <span className="text-red-500">*</span></Label>
                        <div className="border rounded-md">
                            <TextEditor
                                defaultValue={jobData.job_description}
                                onChange={(value: string) => {
                                    updateFormData("job_description", value);
                                }}
                                onBlur={() => onHandleBlur("job_description", jobData.job_description)}
                                minHeight={250}
                                maxHeight={500}
                                initialHeight={350}
                                placeholder="Describe the job role, responsibilities, requirements, and what you're looking for in a candidate..."
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            />
                        </div>
                        <p className="text-sm text-gray-500">
                            The more information you can provide the more likely candidates will be to apply. You can drag the bottom of the editor to adjust its height.
                        </p>
                    </div>

                    {props.isEditMode && !isLoadingUserProfile && (userInfo?.role === "admin" || userInfo?.role === "super_admin") && (
                        <div className="space-y-2">
                            <Label htmlFor="description">Description Masking <span className="text-red-500">*</span></Label>
                            <div className="border rounded-md">
                                <TextEditor
                                    defaultValue={jobData.masked_jd}
                                    onChange={(value: string) => {
                                        updateFormData("masked_jd", value);
                                    }}
                                    onBlur={(value: string) => onHandleBlur("masked_jd", value)}
                                    minHeight={200}
                                    maxHeight={400}
                                    initialHeight={250}
                                    placeholder="Enter the masked version of the job description..."
                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                />
                            </div>
                            <p className="text-sm text-gray-500">
                                Create a masked version of the job description. Drag the bottom edge to resize the editor.
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label htmlFor="commitment">Commitment <span className="text-red-500">*</span></Label>
                            <Select
                                value={jobData.job_type || ""}
                                onValueChange={(value: string) => {
                                    updateFormData("job_type", value);
                                    onHandleBlur("job_type", value);
                                }}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            >
                                <SelectTrigger className="w-full border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:bg-gray-800">
                                    <SelectValue placeholder="Select job commitment type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {listCommitmentTypes.map((item) => (
                                        <SelectItem key={item} value={item}>{item}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="rateType">Rate Type <span className="text-red-500">*</span></Label>
                            <Select
                                value={jobData.pay_rate_frequency}
                                onValueChange={(value: string) => {
                                    updateFormData("pay_rate_frequency", value);
                                    onHandleBlur("pay_rate_frequency", value);
                                }}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select job rate type" />
                                </SelectTrigger>
                                <SelectContent>
                                    {payRateFrequencies.map((item) => (
                                        <SelectItem key={item} value={item}>{item}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Rate Section */}
                    <div className="space-y-4">
                        <Label>Rate <span className="text-red-500">*</span></Label>

                        {useRateRanges ? (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm text-gray-600">Min Rate</Label>
                                        <div className="flex">
                                            <Select
                                                value={jobData.rate_currency}
                                                onValueChange={(value) => {
                                                    updateFormData("rate_currency", value);
                                                    onHandleBlur("rate_currency", jobData.rate_currency)
                                                }}
                                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                            >
                                                <SelectTrigger className="w-20 rounded-r-none border-r-0">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {listCurrency.map((item, index) => (
                                                        <SelectItem key={index} value={item}>{item}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Input
                                                type="text"
                                                className="rounded-l-none"
                                                placeholder="0"
                                                value={jobData.min_rate}
                                                onChange={(e) =>
                                                    updateFormData("min_rate", parseFloat(e.target.value || "0"))
                                                }
                                                onBlur={(e) => onHandleBlur("min_rate", parseFloat(e.target.value || "0"))}
                                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-sm text-gray-600">Max Rate</Label>
                                        <div className="flex">
                                            <Select
                                                value={jobData.rate_currency}
                                                onValueChange={(value: string) => {
                                                    updateFormData("rate_currency", value);
                                                    onHandleBlur("rate_currency", jobData.rate_currency)
                                                }}
                                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                            >
                                                <SelectTrigger className="w-20 rounded-r-none border-r-0">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {listCurrency.map((item, index) => (
                                                        <SelectItem key={index} value={item}>{item}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Input
                                                type="text"
                                                className="rounded-l-none"
                                                placeholder="0"
                                                value={jobData.max_rate}
                                                onChange={(e) =>
                                                    updateFormData("max_rate", parseFloat(e.target.value || "0"))
                                                }
                                                onBlur={(e) => onHandleBlur("max_rate", parseFloat(e.target.value || "0"))}
                                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    type="button"
                                    variant="link"
                                    className="text-blue-600 p-0 h-auto text-sm"
                                    onClick={() => {
                                        setUseRateRanges(false);
                                        updateFormData("fixed_rate", 0);
                                        updateFormData("min_rate", 0);
                                        updateFormData("max_rate", 0);
                                        console.log('reset')
                                    }}
                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                >
                                    Use single rate
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex">
                                    <Select
                                        value={jobData.rate_currency}
                                        onValueChange={(value: string) => {
                                            updateFormData("rate_currency", value);
                                            onHandleBlur("rate_currency", value);
                                        }}
                                        disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                    >
                                        <SelectTrigger className="w-20 rounded-r-none border-r-0">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listCurrency.map((item, index) => (
                                                <SelectItem key={index} value={item}>{item}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        type="text"
                                        className="rounded-l-none"
                                        placeholder="0"
                                        value={jobData.fixed_rate}
                                        onChange={(e) => {
                                            updateFormData("fixed_rate", parseFloat(e.target.value || "0"));
                                        }}
                                        onBlur={(e) => onHandleBlur("fixed_rate", parseFloat(e.target.value || "0"))}
                                        disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-gray-500">
                                        Enter 0 or leave blank to display 'Competitive compensation (DOE)'.
                                    </p>
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="text-blue-600 p-0 h-auto text-sm self-start"
                                        onClick={() => {
                                            setUseRateRanges(true);
                                            updateFormData("fixed_rate", 0);
                                            updateFormData("min_rate", 0);
                                            updateFormData("max_rate", 0);
                                        }}
                                        disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                    >
                                        Use rate ranges
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2 hidden">
                        <Label htmlFor="referralBonus">Referral Bonus</Label>
                        <div className="flex">
                            <Select
                                value={jobData.referral_currency}
                                onValueChange={(value: string) => {
                                    updateFormData("referral_currency", value);
                                    onHandleBlur("referral_currency", jobData.referral_currency)
                                }}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            >
                                <SelectTrigger className="w-20 rounded-r-none border-r-0">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {listCurrency.map((item, index) => (
                                        <SelectItem key={index} value={item}>{item}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                id="referralBonus"
                                type="number"
                                className="rounded-l-none"
                                value={jobData.referral_bonus}
                                onChange={(e) => {
                                    updateFormData("referral_bonus", parseFloat(e.target.value));
                                    onHandleBlur("referral_bonus", parseFloat(e.target.value));
                                }}
                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                            />
                        </div>
                        <p className="text-sm text-gray-500">
                            Add a referral cash incentive for every applicant successfully
                            referred through this listing. Anyone can create and share a
                            referral link.
                        </p>
                    </div>
                </div>
            ) : currentStep === 2 ? (
                // Per-company rollout (#3084): flag ON → Hiring Flow builder
                // (epic #3066); flag OFF → the pre-epic Assessment Flow step,
                // pixel-identical to production before the epic.
                // #3202: the per-job "new hiring flow" toggle (in the header) also gates
                // the builder — OFF ⇒ show the legacy Screening Flow step, even for a
                // company-flow-enabled workspace (config gating).
                hiringFlowEnabled && newHiringFlowEnabled ? (
                <div className="space-y-8 pb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Hiring Flow
                            {props.isEditMode && jobHasStoredFlow && (
                                <span className="ml-2 align-middle text-xs font-medium text-gray-400">v{hiringFlow.version}</span>
                            )}
                        </h2>
                        <p className="text-gray-600">
                            Candidates complete these steps in order. Drag to reorder, add conditions, and set how each step advances.
                        </p>
                    </div>
                    {/* Versioned-edit warning (#3072): in-flight candidates finish on the
                        version they started; saving creates v+1 for new candidates only. */}
                    {props.isEditMode && jobHasStoredFlow && (jobDetail?.hiring_flow_in_flight_count || 0) > 0 && !flowsEqual(hiringFlow, initialHiringFlow) && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <TriangleAlertIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>
                                <b>{jobDetail?.hiring_flow_in_flight_count} candidate{(jobDetail?.hiring_flow_in_flight_count || 0) > 1 ? "s are" : " is"} in this flow on v{initialHiringFlow.version}.</b>{" "}
                                Saving creates v{initialHiringFlow.version + 1} — it applies to new candidates only; the {jobDetail?.hiring_flow_in_flight_count} in flight finish on v{initialHiringFlow.version}. This cannot be merged later.
                            </span>
                        </div>
                    )}
                    <HiringFlowBuilder
                        flow={hiringFlow}
                        onChange={applyFlowChange}
                        assessments={assessments}
                        currentJobId={jobid}
                        mayaEnabled={!!userInfo?.maya_enabled}
                        chatNativeEnabled={!!(jobDetail?.company_chat_native_enabled ?? companyInfo?.chat_native_enabled)}
                        disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                        workAuthMetadata={jobData.work_authorization_metadata}
                        previewJobTitle={jobData.job_title}
                        previewCompanyName={jobData.company_name}
                        whatsappEnabled={companyWhatsappEnabled}
                        previewLocationMetadata={jobData.location_metadata}
                        previewWaCallEnabled={!!jobData.is_enabled_wa_call}
                        onWorkAuthChange={updateWorkAuthorizationMetadata}
                        onRefreshAssessments={loadAssessment}
                    />
                </div>
                ) : (
                <div className="space-y-8 pb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Screening Flow
                        </h2>
                        <p className="text-gray-600">
                            Define the steps candidates must complete before submitting their screening flow.
                        </p>
                    </div>
                    <div className="space-y-12">
                        <div className="grid grid-cols-[320px_1fr] gap-8 items-start pb-12 border-b border-border">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 mb-2">
                                    Screenings
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Add required interview or form screenings.
                                </p>
                            </div>
                            <div className="space-y-6">
                                {/* Interview Section */}
                                <div className="space-y-4">
                                    <Label htmlFor="interview" className="text-sm font-medium">
                                        AI Interview <span className="text-red-500">*</span>
                                    </Label>
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            <Select
                                                value={selectedInterviewAssessment.id ? selectedInterviewAssessment.id : "None"}
                                                onValueChange={(value: string) => {
                                                    if (value === "None") {
                                                        setSelectedInterviewAssessment({ type: "", id: "", name: "None" });
                                                        // Phone Call Interview depends on an AI interview —
                                                        // clearing the interview hides + disables it.
                                                        updateFormData("is_enabled_wa_call", false);
                                                        onHandleBlurAsessment("Interview", value);
                                                        return;
                                                    }
                                                    const selectedAsssessmentInterview = assessments.find((item) => item.id === value && item.type === "Interview");
                                                    setSelectedInterviewAssessment({
                                                        type: "Interview",
                                                        id: (selectedAsssessmentInterview as InterviewAssessment).id || "",
                                                        name: (selectedAsssessmentInterview as InterviewAssessment)?.content.interviewName || ""
                                                    });
                                                    onHandleBlurAsessment("Interview", value);
                                                }}
                                                disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="Select an interview" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {assessments.filter((item) => item.type === "Interview").map((assessment, index) => (
                                                        <SelectItem key={index} value={(assessment as InterviewAssessment).id || ""}>
                                                            {(assessment as InterviewAssessment).content.interviewName}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                                            onClick={() => window.open('/assess', '_blank')}
                                            disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                            Create new interview
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={loadAssessment}
                                        >
                                            <RefreshCcw className="h-2 w-2" />
                                        </Button>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        The selected AI interview is required and must be
                                        completed before submission.
                                    </p>
                                </div>

                                {/* Phone Call Interview — per-job opt-in (#3031). Shown only when the
                                    company has Maya enabled (maya_enabled) AND an AI Interview is
                                    selected. Sends `is_enabled_wa_call`. */}
                                {userInfo?.maya_enabled && selectedInterviewAssessment.id && (
                                <div className="rounded-lg border border-gray-200 p-4">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0">
                                            <Label htmlFor="phone-call-interview" className="text-sm font-medium flex items-center gap-2">
                                                <PhoneIcon className="h-4 w-4 text-blue-600" />
                                                Phone Call Interview
                                            </Label>
                                            <p className="text-sm text-gray-500 mt-1">
                                                Allow the AI to interview candidates on this job by phone call.
                                            </p>
                                        </div>
                                        <Switch
                                            id="phone-call-interview"
                                            checked={jobData.is_enabled_wa_call ?? false}
                                            onCheckedChange={(checked) =>
                                                updateFormData("is_enabled_wa_call", checked)
                                            }
                                            disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                            aria-label="Allow phone call interviews"
                                            className="flex-shrink-0 data-[state=checked]:bg-blue-600"
                                        />
                                    </div>
                                </div>
                                )}

                                {/* Form Section */}
                                <div className="space-y-4">
                                    <Label htmlFor="form" className="text-sm font-medium">
                                        Form
                                    </Label>
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            {!isLoading && (
                                                <Select
                                                    value={selectedAssessment.id ? selectedAssessment.id : "None"}
                                                    onValueChange={(value: string) => {
                                                        if (value === "None") {
                                                            setSelectedAssessment({ type: "", id: "", name: "None" });
                                                            onHandleBlurAsessment("Form", value);
                                                            return;
                                                        }
                                                        const selectedAsssessmentForm = assessments.find((item) => item.id === value && item.type === "Form");
                                                        setSelectedAssessment({
                                                            type: "Form",
                                                            id: (selectedAsssessmentForm as FormAssessment).id || "",
                                                            name: (selectedAsssessmentForm as FormAssessment)?.content.title || ""
                                                        });
                                                        onHandleBlurAsessment("Form", value);
                                                    }}
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="None">None</SelectItem>
                                                        {assessments.filter((item) => item.type === "Form").map((assessment, index) => (
                                                            <SelectItem key={index} value={(assessment as FormAssessment).id || ""}>
                                                                {(assessment as FormAssessment).content.title}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>
                                        <Button
                                            variant="outline"
                                            className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                                            onClick={() => window.open('/assess', '_blank')}
                                            disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                            Create new form
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={loadAssessment}
                                        >
                                            <RefreshCcw className="h-2 w-2" />
                                        </Button>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        Require candidates to complete a form. If no
                                        form is selected, candidates will not see any
                                        forms.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-[320px_1fr] gap-8 items-start">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 mb-2">
                                    Eligibility Checks
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Confirm employment and availability details that may impact
                                    hiring decisions.
                                </p>
                            </div>
                            <div className="space-y-6">
                                <div className="space-y-4">
                                    <div className="space-y-3">
                                        <div className="space-y-1">
                                            <Label className="text-sm font-medium">
                                                Fields to collect
                                            </Label>
                                            <p className="text-sm text-gray-500">
                                                Select only the details relevant to this role. If no details are selected, this step will be skipped.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectEmploymentStatus"
                                                    checked={jobData.work_authorization_metadata?.collect_employment_status || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_employment_status", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectEmploymentStatus"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Current employment status
                                                </Label>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectExpectedSalary"
                                                    checked={jobData.work_authorization_metadata?.collect_expected_salary || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_expected_salary", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectExpectedSalary"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Expected salary
                                                </Label>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectStartAvailability"
                                                    checked={jobData.work_authorization_metadata?.collect_start_availability || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_start_availability", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectStartAvailability"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Earliest available start date
                                                </Label>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectCurrentSalary"
                                                    checked={jobData.work_authorization_metadata?.collect_current_salary || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_current_salary", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectCurrentSalary"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Current or last drawn salary
                                                </Label>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectRelocationWillingness"
                                                    checked={jobData.work_authorization_metadata?.collect_relocation_willingness || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_relocation_willingness", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectRelocationWillingness"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                        Willingness to relocate
                                                </Label>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectWeekendShiftWillingness"
                                                    checked={jobData.work_authorization_metadata?.collect_weekend_shift_willingness || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_weekend_shift_willingness", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectWeekendShiftWillingness"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Availability for weekends or shift work
                                                </Label>
                                            </div>

                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectWorkAuthorization"
                                                    checked={jobData.work_authorization_metadata?.collect_work_authorization || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_work_authorization", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectWorkAuthorization"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Work authorization status
                                                </Label>
                                            </div>

                                            {/* Highest education level (#3085) — normalized ladder;
                                                feeds the hiring-flow education knockout. */}
                                            <div className="flex items-center space-x-2">
                                                <Checkbox
                                                    id="collectEducationLevel"
                                                    checked={jobData.work_authorization_metadata?.collect_education_level || false}
                                                    onCheckedChange={(checked) => {
                                                        updateWorkAuthorizationMetadata("collect_education_level", checked as boolean);
                                                    }}
                                                    className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 data-[state=checked]:text-white"
                                                    disabled={jobData.is_collaborator || isSubscriptionLimitReached}
                                                />

                                                <Label
                                                    htmlFor="collectEducationLevel"
                                                    className="text-sm font-normal cursor-pointer"
                                                >
                                                    Highest education level
                                                </Label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                )
            ) : (currentStep === 3) ? (
                <div className="space-y-6 pb-6">
                    <AccessControlStep
                        data={jobData}
                        isEditMode={props.isEditMode}
                        onChange={(accessControl: JobOwnerItem[]) =>
                            updateFormData("listing_owners", accessControl)
                        }
                    />
                </div>
            ) : (currentStep === 4) && (
                <div className="space-y-6 pb-20">
                    <ConfirmSettingsStep
                        data={jobData}
                        companyInfo={companyInfo}
                        isEditMode={props.isEditMode}
                        setData={setJobData}
                    />
                </div>
            )}
            {/* Progress Tracker with Navigation */}
            {
                !props.isEditMode && (
                    <ProgressTrackerJob
                        steps={tabs}
                        currentStep={currentStep}
                        isDisabled={isDisabledNext}
                        isLoading={isLoadingSubmit}
                        showNextButton={true}
                        onNext={handleNext}
                        onPrevious={handlePrevious}
                        onSubmit={() => handleSubmit()}
                    />
                )
            }

            <JobRecalculatedDialog
                isOpen={isOpenRecalculatedDialog}
                onClose={onCancelEdit}
                onConfirm={onConfirmEdit}
                onDontShowAgain={(isDontShow: boolean) => {
                    console.log('isDontShow', isDontShow)
                    setDontShowAgain(isDontShow);
                }}
            />

            {/* #3147: CV Document Collection warning — same dialog on submit in
                create AND edit. Interrupts save; the user must confirm to proceed. */}
            <Dialog open={cvDocWarnOpen} onOpenChange={setCvDocWarnOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TriangleAlertIcon className="w-5 h-5 text-amber-500" />
                            No way to collect the CV
                        </DialogTitle>
                        <DialogDescription>
                            This hiring flow doesn't require the CV at apply and has no Document
                            Collection step with a CV / resume item — so the candidate will have no
                            way to submit their CV. Add a Document Collection step with a CV item, or
                            save anyway.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setCvDocWarnOpen(false)}>
                            Add the step
                        </Button>
                        <Button
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => {
                                setCvDocWarnOpen(false);
                                handleSubmit(true);
                            }}
                        >
                            Save anyway
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DialogViewLimit
                open={isOpenModalViewLimit}
                onClose={setOpenModalViewLimit}
                title="You're on ourteam Preview"
                description={(
                    <p className="text-gray-500">
                        Browse up to 30 talent profiles and explore how ourteam works.
                        <br /><br />
                        To post a job or unlock full access, just email us at <b>hello@ourteam.ai</b>— we’ll get you started.
                    </p>
                )}
            />

            <JobPreviewDialog
                isOpen={isShowJobPreview}
                onClose={() => setShowJobPreview(false)}
                jobData={jobData}
                selectedAssessmentForm={selectedAssessment.name || ""}
                selectedAssessmentFormId={selectedAssessment.id || ""}
                selectedAssessmentInterview={selectedInterviewAssessment.name || ""}
                selectedAssessmentInterviewId={selectedInterviewAssessment.id || ""}
                companyInfo={companyInfo}
            />
        </>
    )
}